import Layout from '@components/global/Layout';
import Container from '@components/global/Container';
import { useTranslation } from 'next-i18next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { Store } from '@adapters/prismic/fetching/stores/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { GetStaticPropsContext } from 'next';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import StoreCard from '@components/stores/StoreCard';
import Heading from '@components/ui/Heading';
import { useStoresUI } from '@context/StoresContext';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import { useEffect } from 'react';
import styles from './Store.module.scss';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

export async function getStaticPaths() {
  return {
    paths: [{ params: { locale: 'en' } }, { params: { locale: 'fr' } }],
    fallback: false,
  };
}

export const getStaticProps = async ({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>) => {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const previewRef = getPreviewRef(preview, previewData);

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'stores', 'promotions']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  return {
    props: {
      stores,
      preview,
      alternateLocaleUrl: locale === 'en' ? '/fr/stores' : '/en/stores',
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
};

export interface StoresPageProps {
  stores: Store[];
}

export default function StoresPage({ stores }: StoresPageProps) {
  const { t } = useTranslation('stores');
  const { favouriteStoreDetails, setLocationAndFavouriteStore } = useStoresUI();
  const favouriteStoreId = favouriteStoreDetails?.data?.mec_store_id;

  useEffect(() => {
    if (favouriteStoreDetails) {
      setLocationAndFavouriteStore(favouriteStoreDetails.data.geo);
    }
    // Keyed on the favourite store id, not the details object: the dispatch below
    // replaces that object, so depending on it would re-run this effect forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouriteStoreId, setLocationAndFavouriteStore]);

  return (
    <Container>
      {favouriteStoreDetails && (
        <>
          <div className={styles.topHeadingContainer}>
            <Heading semanticLevel="h1" styleLevel="1">
              {t('stores.myStore')}
            </Heading>
          </div>
          <div className={styles.myStore}>
            <StoreCard
              storesData={favouriteStoreDetails}
              path={favouriteStoreDetails?.uid}
            />
          </div>
        </>
      )}
      <div className={styles.headingContainer}>
        <Heading semanticLevel="h2" styleLevel="1">
          {t('stores.store')}
        </Heading>
      </div>
      <ul className={styles.storesListContainer}>
        {stores?.map((store) => (
          <li key={`store-${store.uid}`} className={styles.storesListItem}>
            <StoreCard storesData={store} path={store.uid} />
          </li>
        ))}
      </ul>
    </Container>
  );
}

StoresPage.Layout = Layout;
